<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2 pt-4'">
    <!-- Current Balance Card -->
    <v-card class="mb-4 ordera-box-shadow">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="6">
            <div class="text-h6 grey--text mb-2">{{ $t("current_balance") }}</div>
            <div class="text-h4 primary--text font-weight-bold">
              {{ formatCurrency(currentBalance) }}
            </div>
          </v-col>
          <v-col cols="12" sm="6" class="text-sm-right">
            <v-icon size="64" color="primary" class="opacity-20">mdi-wallet</v-icon>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Top-up Form Card -->
    <v-row>
      <v-col cols="12" xl="6">
        <v-card class="ordera-box-shadow">
          <v-card-title>
            <v-icon left color="primary">mdi-cash-plus</v-icon>
            {{ $t("add_credit") }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-form ref="form" v-model="valid" @submit.prevent="submitTopup">
              <v-row>
                <!-- Amount Input -->
                <v-col cols="12">
                  <text-field
                    v-model="topupAmount"
                    :label="$t('amount')"
                    type="number"
                    :rules="amountRules"
                    prepend-inner-icon="mdi-cash"
                    :suffix="restaurantCurrency"
                    outlined
                    dense
                  />
                </v-col>

                <!-- Payment Method -->
                <v-col cols="12">
                  <autocomplete
                    v-model="paymentMethod"
                    :items="paymentMethods"
                    :label="$t('payment_method')"
                    item-text="label"
                    item-value="value"
                    :rules="[(v) => !!v || $t('required_field')]"
                    prepend-inner-icon="mdi-credit-card"
                    outlined
                    dense
                  />
                </v-col>

                <!-- Quick Amount Buttons -->
                <v-col cols="12">
                  <div class="text-caption font-weight-medium">
                    {{ $t("quick_amounts") }}
                  </div>
                  <v-chip-group v-model="selectedQuickAmount" mandatory>
                    <v-chip
                      v-for="amount in quickAmounts"
                      :key="amount"
                      :value="amount"
                      color="primary"
                      @click="setQuickAmount(amount)"
                    >
                      {{ amount }} {{ restaurantCurrency }}
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <!-- Submit Button -->
                <v-col cols="12">
                  <primary-button
                    @click="submitTopup"
                    :disabled="!valid || submitting"
                    :loading="submitting"
                    block
                    color="success"
                  >
                    <v-icon left>mdi-plus-circle</v-icon>
                    {{ $t("add_credit") }}
                  </primary-button>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Recent Top-ups -->
      <v-col cols="12" xl="6">
        <v-card
          class="ordera-box-shadow"
          :class="{ 'mt-4': $vuetify.breakpoint.lgAndDown }"
          v-if="recentTopups.length > 0"
        >
          <v-card-title>
            <v-icon left color="grey">mdi-history</v-icon>
            {{ $t("recent_topups") }}
          </v-card-title>
          <v-divider></v-divider>
          <v-list two-line>
            <template v-for="(topup, index) in recentTopups">
              <v-list-item>
                <v-list-item-avatar color="success" size="40">
                  <v-icon dark>mdi-plus</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">
                    +{{ formatCurrency(topup.amount) }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ formatDate(topup.date) }} • {{ topup.payment_method }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip
                    small
                    :color="topup.status === 'completed' ? 'success' : 'warning'"
                    dark
                  >
                    {{ $t(topup.status) }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
              <v-divider
                v-if="index < recentTopups.length - 1"
                :key="`divider-${topup.id}`"
              ></v-divider>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "CustomerReportTabTopup",
  mixins: [hasSnackbar],
  data() {
    return {
      valid: false,
      submitting: false,
      topupAmount: null,
      paymentMethod: null,
      description: "",
      selectedQuickAmount: null,
      quickAmounts: [10, 20, 50, 100, 200],
      currentBalance: 150.0, // TODO: Get from API
      recentTopups: [
        {
          id: 1,
          date: "2026-01-10",
          amount: 50.0,
          payment_method: "Gotovina",
          status: "completed",
        },
        {
          id: 2,
          date: "2026-01-05",
          amount: 100.0,
          payment_method: "Kartica",
          status: "completed",
        },
      ],
    };
  },
  computed: {
    paymentMethods() {
      return [
        { label: this.$t("cash"), value: "cash" },
        { label: this.$t("card"), value: "card" },
        { label: this.$t("bank_transfer"), value: "transfer" },
      ];
    },
    amountRules() {
      return [
        (v) => !!v || this.$t("required_field"),
        (v) => v > 0 || this.$t("amount_must_be_positive"),
        (v) => v <= 1000 || this.$t("max_amount_exceeded", { currency: this.restaurantCurrency }),
      ];
    },
  },
  methods: {
    setQuickAmount(amount) {
      this.topupAmount = amount;
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "0.00";
      return parseFloat(amount).toFixed(2);
    },
    formatDate(date) {
      if (!date) return "";
      return this.localeDate(date);
    },
    async submitTopup() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.submitting = true;
      try {
        // TODO: Implement API call
        // const response = await creditService.addCredit({
        //   amount: this.topupAmount,
        //   payment_method: this.paymentMethod,
        //   description: this.description
        // });

        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Update current balance (mock)
        this.currentBalance += parseFloat(this.topupAmount);

        // Add to recent topups
        this.recentTopups.unshift({
          id: Date.now(),
          date: new Date().toISOString().substr(0, 10),
          amount: parseFloat(this.topupAmount),
          payment_method:
            this.paymentMethods.find((m) => m.value === this.paymentMethod)?.label ||
            this.paymentMethod,
          status: "completed",
        });

        this.showSuccessSnackbar(this.$t("credit_added_successfully"));
        this.resetForm();
      } catch (error) {
        console.error("Error adding credit:", error);
        this.showErrorSnackbar(error, this.$t("error_adding_credit"));
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.topupAmount = null;
      this.paymentMethod = null;
      this.description = "";
      this.selectedQuickAmount = null;
      this.$refs.form?.resetValidation();
    },
  },
};
</script>

<style scoped>
.opacity-20 {
  opacity: 0.2;
}
</style>
